import React from "react";
import { Menu } from "@headlessui/react";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { routes } from "../config/utility";

export const UserCard = ({ user, handleEdit, handleDelete }) => {
    return (
        <div
            key={user.id}
            className="w-full flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 p-4 bg-white border border-gray-200 rounded-lg shadow-sm"
        >
            {/* Left side - Profile Image and Details */}
            <div className="flex items-center space-x-4 w-full sm:w-auto">
                <img 
                    src={user.profile_photo ? user.profile_photo : 'https://via.placeholder.com/150'} 
                    alt="User" 
                    className="w-16 h-16 sm:w-24 sm:h-24 rounded object-cover"
                />
                <div>
                    <p className="text-xs sm:text-sm text-gray-500">#{user.id}</p>
                    <p className="text-base sm:text-lg font-semibold">{user.name}</p>
                    <p className="text-xs sm:text-sm text-gray-500">Join On {user.join_at}</p>
                    {/* Roles Section */}
                    <div className="flex flex-wrap gap-1 mt-1">
                        {user.roles && user.roles.map((role, index) => (
                            <span
                                key={index}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                                    bg-gradient-to-r from-purple-500 to-purple-600 text-white
                                    shadow-sm transition-all duration-200 hover:scale-105"
                            >
                                {role.toUpperCase()}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Middle section - Contact Info */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 w-full sm:flex-1">
                {/* Username */}
                <div className="flex flex-col space-y-1">
                    <div>
                        <p className="text-xs sm:text-sm text-gray-500">Username</p>
                        <p className="text-sm sm:text-base font-semibold truncate">{user.username}</p>
                    </div>
                </div>

                {/* Phone */}
                <div className="flex flex-col space-y-1">
                    <div className="flex items-center space-x-2">
                        <p className="text-xs sm:text-sm text-gray-500">Phone</p>
                        {user.phone_verified_at && (
                            <span className="flex items-center text-green-600">
                                <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="text-xs font-medium text-green-600 ml-1 hidden sm:inline">Verified</span>
                            </span>
                        )}
                    </div>
                    <p className="text-sm sm:text-base font-semibold truncate">{user.phone}</p>
                </div>

                {/* Email */}
                <div className="flex flex-col space-y-1 col-span-2 sm:col-span-1">
                    <div className="flex items-center space-x-2">
                        <p className="text-xs sm:text-sm text-gray-500">Email</p>
                        {user.email_verified_at && (
                            <span className="flex items-center text-green-600">
                                <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="text-xs font-medium text-green-600 ml-1 hidden sm:inline">Verified</span>
                            </span>
                        )}
                    </div>
                    <p className="text-sm sm:text-base font-semibold truncate">{user.email}</p>
                </div>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto mt-4 sm:mt-0">
                <Link 
                    to={routes.user_detail.replace(':email?', user.email)} 
                    className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-white text-sm bg-purple-600 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 text-center"
                >
                    View Details
                </Link>

                {/* 3-dot menu */}
                <Menu as="div" className="relative">
                    <Menu.Button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                            stroke="currentColor"
                            className="w-5 h-5"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 6.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12 12.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12 18.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
                            />
                        </svg>
                    </Menu.Button>

                    <Menu.Items className="absolute right-0 mt-2 w-36 bg-white shadow-lg rounded-lg border border-gray-200 z-10 focus:outline-none">
                        <div className="py-1">
                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        className={`${
                                            active ? "bg-purple-50 text-purple-700" : "text-gray-700"
                                        } group flex w-full items-center justify-center px-4 py-2 text-sm`}
                                        onClick={handleEdit}
                                    >
                                        Edit
                                    </button>
                                )}
                            </Menu.Item>

                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        className={`${
                                            active ? "bg-red-50 text-red-700" : "text-gray-700"
                                        } group flex w-full items-center justify-center px-4 py-2 text-sm`}
                                        onClick={handleDelete}
                                    >
                                        Delete
                                    </button>
                                )}
                            </Menu.Item>
                        </div>
                    </Menu.Items>
                </Menu>
            </div>
        </div>
    );
};
